import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Popper,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReportsService from '../../../Services/reports.service';
import DocumentService from '../../../Services/documents.service';
import { JournalEntryTypes } from '../../../assests/Constants/journalEntryTypes';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import BiMonthlyPeriodModal from '../../ClientDetails/Modals/BiMonthlyPeriodModal';
import fieldStyles from './AddJournalEntryModal.module.scss';
import modalStyles from './JournalEntryTemplatesModal.module.scss';
import styles from './CompoundJournalModals.module.scss';

const BI_MONTHLY = 'דו חודשי';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const pad2 = (n) => String(n).padStart(2, '0');
const utcMonthStart = (year, monthIndex) => new Date(Date.UTC(year, monthIndex, 1));
const utcMonthEnd = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
const toDateOnly = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const toMonthLabel = (d) => `${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
const toUtcNoonIso = (d) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12)).toISOString();

/** VAT period containing the given UTC month: the month itself, or its Jan-Feb/Mar-Apr/... block. */
const periodForMonth = (d, isBiMonthly) => {
    if (!isBiMonthly) {
        const month = utcMonthStart(d.getUTCFullYear(), d.getUTCMonth());
        return { start: month, end: month };
    }
    const start = utcMonthStart(d.getUTCFullYear(), d.getUTCMonth() - (d.getUTCMonth() % 2));
    const end = utcMonthStart(start.getUTCFullYear(), start.getUTCMonth() + 1);
    return { start, end };
};

/** Previous VAT period relative to today. */
const getDefaultPeriod = (isBiMonthly) => {
    const now = new Date();
    const currentStart = periodForMonth(utcMonthStart(now.getFullYear(), now.getMonth()), isBiMonthly).start;
    const prevMonth = utcMonthStart(currentStart.getUTCFullYear(), currentStart.getUTCMonth() - 1);
    return periodForMonth(prevMonth, isBiMonthly);
};

const storageKey = (caseId) => `vatClosingCurrentAccount_${caseId}`;

const VatClosingJournalModal = ({
    open,
    onClose,
    client,
    token,
    direction = 'rtl',
    accountCardOptions = [],
    settings,
    onCreated,
}) => {
    const { t } = useTranslation();
    const isBiMonthly = client?.reportingType === BI_MONTHLY;
    const textAlign = direction === 'rtl' ? 'right' : 'left';
    const inputProps = useMemo(() => ({ style: { textAlign } }), [textAlign]);
    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const [period, setPeriod] = useState(() => getDefaultPeriod(isBiMonthly));
    const [showBiMonthPicker, setShowBiMonthPicker] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [reportMissing, setReportMissing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [outputVat, setOutputVat] = useState('');
    const [inputVat, setInputVat] = useState('');
    const [equipmentVat, setEquipmentVat] = useState('');
    const [currentAmount, setCurrentAmount] = useState('');
    const [currentOverridden, setCurrentOverridden] = useState(false);
    const [currentAccount, setCurrentAccount] = useState('');

    const [documentDate, setDocumentDate] = useState(null);
    const [reportingMonth, setReportingMonth] = useState(null);
    const [reference, setReference] = useState('');
    const [description, setDescription] = useState('');

    const incomeVatCardId = String(settings?.incomeVatCardId ?? '1');
    const expenseVatCardId = String(settings?.expenseVatCardId ?? '3');
    const expenseEquipmentVatCardId = String(settings?.expenseEquipmentVatCardId ?? '2');
    const sortCodeId = String(settings?.sortCodeDebitCredit ?? '3');

    const cardOptions = useMemo(() => (
        (accountCardOptions || [])
            .filter((c) => c && c.cardId != null && c.cardId !== '')
            .map((c) => ({ value: String(c.cardId), label: `${c.cardId} - ${c.name || ''}` }))
    ), [accountCardOptions]);

    const cardLabel = useCallback(
        (cardId) => cardOptions.find((o) => o.value === String(cardId))?.label || String(cardId),
        [cardOptions]
    );

    useEffect(() => {
        if (!open) return;
        setPeriod(getDefaultPeriod(isBiMonthly));
        setShowBiMonthPicker(false);
        setCurrentOverridden(false);
        const saved = client?.caseId ? localStorage.getItem(storageKey(client.caseId)) : '';
        setCurrentAccount(saved || '');
    }, [open, isBiMonthly, client?.caseId]);

    useEffect(() => {
        if (!open || !period.start || !period.end) return;
        const end = utcMonthEnd(period.end);
        const periodLabel = toDateOnly(period.start) === toDateOnly(period.end)
            ? toMonthLabel(period.end)
            : `${toMonthLabel(period.start)}-${toMonthLabel(period.end)}`;
        setDocumentDate(end);
        setReportingMonth(period.end);
        setReference(periodLabel);
        setDescription(`${t('VatClosing') || 'סגירת מע"מ'} ${periodLabel}`);
    }, [open, period.start, period.end, t]);

    const loadReport = useCallback(async () => {
        if (!client?.caseId || !token || !period.start || !period.end) return;
        setLoadingReport(true);
        try {
            const res = await ReportsService.getVatByDateRange({
                CaseId: client.caseId,
                StartDate: toDateOnly(period.start),
                EndDate: toDateOnly(utcMonthEnd(period.end)),
                IsRange: false,
            }, token);
            const data = res.ok ? await res.json() : null;
            const output = round2(data?.vatTransactions ?? data?.VATTransactions);
            const input = round2(data?.otherVATTaxes ?? data?.OtherVATTaxes);
            const equipment = round2(data?.equipmentVAT ?? data?.EquipmentVAT);
            const status = data?.status ?? data?.Status;
            const isReported = status === 1 || status === 'Reported';
            setReportMissing(!data || !isReported || (output === 0 && input === 0 && equipment === 0));
            setOutputVat(output);
            setInputVat(input);
            setEquipmentVat(equipment);
            setCurrentOverridden(false);
        } catch (err) {
            console.error('Failed to load reported VAT:', err);
            setReportMissing(true);
        } finally {
            setLoadingReport(false);
        }
    }, [client?.caseId, token, period.start, period.end]);

    useEffect(() => {
        if (open) loadReport();
    }, [open, loadReport]);

    const calculatedCurrent = round2(round2(outputVat) - (round2(inputVat) + round2(equipmentVat)));

    useEffect(() => {
        if (!currentOverridden) setCurrentAmount(calculatedCurrent);
    }, [calculatedCurrent, currentOverridden]);

    const totalDebit = round2(outputVat);
    const totalCredit = round2(round2(inputVat) + round2(equipmentVat) + round2(currentAmount));
    const difference = round2(totalDebit - totalCredit);
    const isBalanced = difference === 0;
    const canApprove =
        isBalanced && totalDebit !== 0 && !!currentAccount && !!documentDate && !!reportingMonth && !saving && !loadingReport;

    const handleCurrentAccountChange = (value) => {
        setCurrentAccount(value);
        if (client?.caseId) {
            if (value) localStorage.setItem(storageKey(client.caseId), value);
            else localStorage.removeItem(storageKey(client.caseId));
        }
    };

    const handleApprove = async () => {
        if (!canApprove) return;
        const reportingIso = new Date(Date.UTC(
            reportingMonth.getUTCFullYear(), reportingMonth.getUTCMonth(), 10, 12
        )).toISOString();
        const base = {
            caseId: client.caseId,
            accountantId: client.accountantId,
            sortCodeId,
            description,
            reference,
            documentDate: toUtcNoonIso(documentDate),
            uploadDate: new Date().toISOString(),
            reportingMonthDate: reportingIso,
            registrationMonthDate: reportingIso,
            debitAccount: incomeVatCardId,
            vat: 0,
            journalEntryType: JournalEntryTypes.VatClosing,
        };
        const rows = [
            { ...base, creditAccount: expenseVatCardId, amount: round2(inputVat) },
            { ...base, creditAccount: expenseEquipmentVatCardId, amount: round2(equipmentVat) },
            { ...base, creditAccount: currentAccount, amount: round2(currentAmount) },
        ].filter((r) => r.amount !== 0);

        setSaving(true);
        try {
            const res = await DocumentService.UploadDocumentPayloads('JournalEntries', rows, token);
            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Create failed');
            }
            toast.success(t('VatClosingCreated') || 'VAT closing journal entry created');
            onClose();
            if (onCreated) await onCreated();
        } catch (err) {
            toast.error(err?.message || t('VatClosingCreateFailed') || 'Failed to create VAT closing journal entry');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const renderAmount = (value, onChange) => (
        <TextField
            size="small"
            variant="standard"
            fullWidth
            type="number"
            className={fieldStyles.styleInput}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            InputProps={comboInputProps}
            inputProps={{ step: '0.01', style: { textAlign } }}
        />
    );

    const renderTextField = (value, onChange, placeholder) => (
        <TextField
            size="small"
            variant="standard"
            fullWidth
            className={fieldStyles.styleInput}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            InputProps={comboInputProps}
            inputProps={inputProps}
        />
    );

    const currentAccountCombo = (
        <Autocomplete
            className={fieldStyles.combo}
            size="small"
            fullWidth
            options={cardOptions}
            value={cardOptions.find((o) => o.value === currentAccount) || null}
            isOptionEqualToValue={(o, v) => o?.value === v?.value}
            getOptionLabel={(o) => o?.label ?? ''}
            onChange={(_, v) => handleCurrentAccountChange(v ? v.value : '')}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={t('SelectAccountCard') || 'Select account card'}
                    className={`${fieldStyles.styleInput}${!currentAccount ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                    inputProps={{ ...params.inputProps, ...inputProps }}
                    InputProps={{ ...params.InputProps, ...comboInputProps }}
                />
            )}
            ListboxProps={{ direction }}
            PopperComponent={renderComboPopper}
        />
    );

    const lines = [
        {
            key: 'output',
            label: t('OutputVat') || 'מע"מ עסקאות',
            card: cardLabel(incomeVatCardId),
            debit: renderAmount(outputVat, setOutputVat),
        },
        {
            key: 'input',
            label: t('InputVat') || 'מע"מ תשומות',
            card: cardLabel(expenseVatCardId),
            credit: renderAmount(inputVat, setInputVat),
        },
        {
            key: 'equipment',
            label: t('EquipmentInputVat') || 'מע"מ תשומות ציוד',
            card: cardLabel(expenseEquipmentVatCardId),
            credit: renderAmount(equipmentVat, setEquipmentVat),
        },
        {
            key: 'current',
            label: t('VatCurrentAccount') || 'מע"מ חו"ז',
            card: currentAccountCombo,
            credit: (
                <div className={styles.currentAmount}>
                    {renderAmount(currentAmount, (v) => { setCurrentAmount(v); setCurrentOverridden(true); })}
                    <Tooltip title={t('Recalculate') || 'Recalculate'}>
                        <span>
                            <IconButton size="small" onClick={() => setCurrentOverridden(false)} disabled={!currentOverridden}>
                                <RestartAltIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={saving ? undefined : onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal} ${styles.modalWide}`}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon} disabled={saving}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('VatClosing') || 'סגירת מע"מ'}</div>
                                <div />
                            </div>

                            <div className={modalStyles.scroll}>
                                <div className={styles.periodRow} dir={direction}>
                                    <div className={modalStyles.dateField}>
                                        <span className={modalStyles.fieldLabel}>{t('Period') || 'Period'}</span>
                                        {isBiMonthly ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className={styles.periodTrigger}
                                                    onClick={() => setShowBiMonthPicker(true)}
                                                >
                                                    <CalendarTodayOutlinedIcon className={styles.periodIcon} />
                                                    <span>{toMonthLabel(period.start)} - {toMonthLabel(period.end)}</span>
                                                </button>
                                                <BiMonthlyPeriodModal
                                                    visible={showBiMonthPicker}
                                                    onClose={() => setShowBiMonthPicker(false)}
                                                    date={period.start}
                                                    t={t}
                                                    direction={direction}
                                                    onDateChange={(s) => s && setPeriod(periodForMonth(s, true))}
                                                />
                                            </>
                                        ) : (
                                            <CustomMonthDatePicker
                                                date={period.start}
                                                onDateChange={(d) => d && setPeriod(periodForMonth(d, false))}
                                                height="40px"
                                                width="100%"
                                            />
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={modalStyles.ghostBtn}
                                        onClick={loadReport}
                                        disabled={loadingReport}
                                    >
                                        {loadingReport
                                            ? <CircularProgress className={modalStyles.spinner} size={18} />
                                            : (t('LoadFromVatReport') || 'Load from VAT report')}
                                    </button>
                                </div>

                                {reportMissing && !loadingReport && (
                                    <div className={styles.warning}>
                                        {t('NoSubmittedVatReport') || 'No submitted VAT report was found for this period. You can enter the amounts manually.'}
                                    </div>
                                )}

                                <p className={modalStyles.sectionTitle} style={{ marginBottom: 8 }}>
                                    {t('SuggestedJournalEntry') || 'Suggested journal entry'}
                                </p>
                                <div className={modalStyles.tableWrap}>
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        sx={{ '& .MuiTableCell-root': { py: 1, px: 1.5, textAlign, verticalAlign: 'middle' } }}
                                    >
                                        <Table size="small" dir={direction} sx={{ minWidth: 760 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                                                    <TableCell align={textAlign}>{t('AccountCard') || 'Account card'}</TableCell>
                                                    <TableCell align={textAlign} className={styles.amountCell}>{t('Debit') || 'Debit'}</TableCell>
                                                    <TableCell align={textAlign} className={styles.amountCell}>{t('Credit') || 'Credit'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {lines.map((line) => (
                                                    <TableRow key={line.key} hover>
                                                        <TableCell align={textAlign}>{line.label}</TableCell>
                                                        <TableCell align={textAlign}>{line.card}</TableCell>
                                                        <TableCell align={textAlign}>{line.debit || '—'}</TableCell>
                                                        <TableCell align={textAlign}>{line.credit || '—'}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell align={textAlign} colSpan={2} sx={{ fontWeight: 700 }}>{t('Total') || 'Total'}</TableCell>
                                                    <TableCell align={textAlign} sx={{ fontWeight: 700 }}>{beautifyNumber(totalDebit)}</TableCell>
                                                    <TableCell align={textAlign} sx={{ fontWeight: 700 }}>{beautifyNumber(totalCredit)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>

                                <div className={`${styles.balance} ${isBalanced ? styles.balanceOk : styles.balanceError}`}>
                                    <span>{t('TotalDebit') || 'Total debit'}: {beautifyNumber(totalDebit)}</span>
                                    <span>{t('TotalCredit') || 'Total credit'}: {beautifyNumber(totalCredit)}</span>
                                    <span>{t('Difference') || 'Difference'}: {beautifyNumber(difference)}</span>
                                    {!isBalanced && <span>{t('EntryNotBalanced') || 'The entry is not balanced'}</span>}
                                </div>

                                <div className={modalStyles.dateRow} dir={direction} style={{ marginTop: 16 }}>
                                    <div className={modalStyles.dateField}>
                                        <span className={modalStyles.fieldLabel}>{t('DocumentDate') || 'Document date'}</span>
                                        <CustomDatePicker
                                            date={documentDate}
                                            onDateChange={(d) => d && setDocumentDate(d)}
                                            height="40px"
                                            label=""
                                        />
                                    </div>
                                    <div className={modalStyles.dateField}>
                                        <span className={modalStyles.fieldLabel}>{t('MonthReportDate') || 'Reporting month'}</span>
                                        <CustomMonthDatePicker
                                            date={reportingMonth}
                                            onDateChange={(d) => d && setReportingMonth(d)}
                                            height="40px"
                                            width="100%"
                                        />
                                    </div>
                                    <div className={modalStyles.formField}>
                                        <span className={modalStyles.fieldLabel}>{t('Reference') || 'Reference'}</span>
                                        {renderTextField(reference, setReference, t('Reference') || 'Reference')}
                                    </div>
                                    <div className={`${modalStyles.formField} ${modalStyles.formFieldWide}`}>
                                        <span className={modalStyles.fieldLabel}>{t('Description') || 'Description'}</span>
                                        {renderTextField(description, setDescription, t('Description') || 'Description')}
                                    </div>
                                </div>
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose} disabled={saving}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                <button
                                    type="button"
                                    className={modalStyles.primaryBtn}
                                    disabled={!canApprove}
                                    onClick={handleApprove}
                                >
                                    {saving
                                        ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                        : (t('Approve') || 'Approve')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default VatClosingJournalModal;
